import React, { useState, useEffect } from 'react';
import {
    getAllRoleDomainMappings,
    getRoleDomainMappingById,
    createRoleDomainMapping,
    updateRoleDomainMapping,
    getAllDomainsLookup
} from './services/masterDataService';
import { useUserRoles,useDomains } from './hooks';

const c = {
    ink:          '#0F172A',
    inkSoft:      '#334155',
    muted:        '#64748B',
    faint:        '#94A3B8',
    pageBg:       '#f0fdf9',
    surface:      '#FFFFFF',
    border:       '#E2E8F0',
    borderSoft:   '#F1F5F9',
    accent:       '#0aa375',
    accentLight:  '#ECFDF5',
    accentMid:    '#34D399',
    success:      '#059669',
    successLight: '#ECFDF5',
    successBorder:'#6EE7B7',
    danger:       '#E11D48',
    dangerLight:  '#FFF1F2',
    dangerBorder: '#FDA4AF',
    info:         '#0284C7',
    infoLight:    '#F0F9FF',
};

const sans = "'Inter', system-ui, -apple-system, sans-serif";
const mono = "'JetBrains Mono', 'SF Mono', ui-monospace, monospace";

const getSessionUsername = () => sessionStorage.getItem('username') || 'System';

const GlobalKeyframes = () => (
    <style>{`
        @keyframes rdm-spin { to { transform: rotate(360deg); } }
        @keyframes rdm-fade-in { from { opacity: 0; transform: translateY(3px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes rdm-pulse { 0%, 100% { opacity: 0.55; } 50% { opacity: 1; } }
    `}</style>
);

const Spinner = ({ size = 18, color = c.accent, trackColor = c.borderSoft, thickness = 2.5 }) => (
    <span
        style={{
            display: 'inline-block',
            width: size,
            height: size,
            borderRadius: '50%',
            border: `${thickness}px solid ${trackColor}`,
            borderTopColor: color,
            animation: 'rdm-spin 0.7s linear infinite',
            flexShrink: 0,
        }}
    />
);

const InlineSpinnerText = ({ label, size = 14 }) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', color: c.muted, fontSize: '12.5px' }}>
        <Spinner size={size} thickness={2} />
        {label}
    </span>
);

const Badge = ({ type, label }) => {
    const map = {
        success: { bg: c.successLight, color: c.success, border: c.successBorder },
        danger:  { bg: c.dangerLight,  color: c.danger,  border: c.dangerBorder  },
        accent:  { bg: c.accentLight,  color: c.accent,  border: c.accentMid     },
        neutral: { bg: '#fcf8f8',      color: c.muted,   border: c.border        },
    };
    const s = map[type] || map.neutral;
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: '5px',
            padding: '3px 10px', borderRadius: '99px',
            fontSize: '11.5px', fontWeight: 600,
            background: s.bg, color: s.color, border: `1px solid ${s.border}`,
        }}>
            <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: s.color, flexShrink: 0 }} />
            {label}
        </span>
    );
};

const Toggle = ({ checked, onChange }) => (
    <label style={{ position: 'relative', width: '40px', height: '22px', flexShrink: 0, cursor: 'pointer' }}>
        <input type="checkbox" checked={checked} onChange={onChange}
            style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }} />
        <span style={{
            position: 'absolute', inset: 0,
            background: checked ? c.accent : '#CBD5E1',
            borderRadius: '99px', transition: '0.2s ease',
        }}>
            <span style={{
                position: 'absolute', height: '16px', width: '16px',
                left: checked ? '21px' : '3px', top: '3px',
                background: '#fff', borderRadius: '50%', transition: '0.2s ease',
                boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
            }} />
        </span>
    </label>
);

const ToggleRow = ({ label, desc, checked, onChange }) => (
    <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '11px 0', borderBottom: `1px solid ${c.borderSoft}`,
    }}>
        <div>
            <div style={{ fontSize: '13px', fontWeight: 500, color: c.inkSoft }}>{label}</div>
            {desc && <div style={{ fontSize: '11px', color: c.faint, marginTop: '1px' }}>{desc}</div>}
        </div>
        <Toggle checked={checked} onChange={onChange} />
    </div>
);

const StatCard = ({ label, value, icon, bg, iconBg, iconColor, borderColor, loading }) => (
    <div style={{
        background: bg, border: `1px solid ${borderColor || c.border}`,
        borderRadius: '12px', padding: '0.6rem 0.9rem',
        display: 'flex', alignItems: 'center', gap: '10px',
        flexShrink: 0,
    }}>
        <div style={{
            width: '36px', height: '36px', borderRadius: '10px', flexShrink: 0,
            background: iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '16px',
        }}>
            {icon}
        </div>
        <div>
            <div style={{ fontSize: '10px', fontWeight: 600, color: iconColor, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '1px' }}>{label}</div>
            {loading ? (
                <div style={{ width: '28px', height: '18px', borderRadius: '5px', background: borderColor || c.border, opacity: 0.5, animation: 'rdm-pulse 1.3s ease-in-out infinite' }} />
            ) : (
                <div style={{ fontSize: '22px', fontWeight: 700, color: c.ink, fontFamily: mono, letterSpacing: '-0.5px', lineHeight: 1.1 }}>{value}</div>
            )}
        </div>
    </div>
);

const FormField = ({ label, children }) => (
    <div style={{ marginBottom: '1rem' }}>
        <label style={{
            display: 'block', fontSize: '12px', fontWeight: 600, color: c.inkSoft,
            marginBottom: '6px', letterSpacing: '0.2px',
        }}>{label}</label>
        {children}
    </div>
);

const inputStyle = {
    width: '100%', padding: '9px 12px', borderRadius: '10px', boxSizing: 'border-box',
    border: `1.5px solid ${c.border}`, background: c.surface,
    color: c.ink, fontSize: '13px', fontFamily: sans,
    outline: 'none', transition: 'border-color 0.15s',
};

const btnBase = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
    padding: '8px 16px', borderRadius: '10px', fontSize: '13px', fontWeight: 500,
    cursor: 'pointer', border: `1.5px solid ${c.border}`, background: c.surface,
    color: c.inkSoft, fontFamily: sans, transition: 'all 0.15s ease',
};
const btnPrimary = {
    ...btnBase,
    background: c.accent, borderColor: c.accent, color: '#fff',
    boxShadow: '0 2px 8px rgba(10,163,117,0.3)',
};
const iconBtn = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: '32px', height: '32px', borderRadius: '8px',
    cursor: 'pointer', border: `1.5px solid ${c.border}`, background: c.surface,
    color: c.muted, fontFamily: sans, transition: 'all 0.15s ease', fontSize: '14px',
};

const Toast = ({ message, type, visible }) => {
    if (!visible) return null;
    const map = {
        success: { bg: c.successLight, color: c.success, border: c.successBorder, icon: '✓' },
        error:   { bg: c.dangerLight,  color: c.danger,  border: c.dangerBorder,  icon: '✕' },
        info:    { bg: c.infoLight,    color: c.info,    border: '#BAE6FD',       icon: 'ℹ' },
    };
    const s = map[type] || map.info;
    return (
        <div style={{
            position: 'fixed', bottom: '24px', right: '24px',
            background: s.bg, color: s.color, border: `1px solid ${s.border}`,
            borderRadius: '12px', padding: '12px 18px', fontSize: '13px', fontWeight: 500,
            display: 'flex', alignItems: 'center', gap: '10px',
            zIndex: 9999, maxWidth: '320px',
            boxShadow: '0 8px 30px rgba(0,0,0,0.1)',
            animation: 'rdm-fade-in 0.2s ease',
        }}>
            <span style={{ fontSize: '16px' }}>{s.icon}</span>
            {message}
        </div>
    );
};

const Modal = ({ visible, onClose, title, children, footer }) => {
    useEffect(() => {
        const h = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', h);
        return () => document.removeEventListener('keydown', h);
    }, [onClose]);
    if (!visible) return null;
    return (
        <div style={{
            position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
            backdropFilter: 'blur(4px)',
        }} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
            <div style={{
                background: c.surface, borderRadius: '18px', border: `1px solid ${c.border}`,
                padding: '1rem', width: '460px', maxWidth: '95vw',
                boxShadow: '0 32px 80px rgba(0,0,0,0.2)',
                animation: 'rdm-fade-in 0.18s ease',
            }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem' }}>
                    <h3 style={{ fontSize: '16px', fontWeight: 700, color: c.ink, fontFamily: sans, margin: 0 }}>{title}</h3>
                    <button onClick={onClose} style={{ ...iconBtn, border: 'none', background: c.borderSoft, fontSize: '15px' }}>✕</button>
                </div>
                {children}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '1.25rem', paddingTop: '1rem', borderTop: `1px solid ${c.border}` }}>
                    {footer}
                </div>
            </div>
        </div>
    );
};

const RoleDomainMappings = () => {
    const [mappings, setMappings]         = useState([]);
    const [loading, setLoading]           = useState(false);
    const [search, setSearch]             = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [toast, setToast]               = useState({ visible: false, message: '', type: 'success' });
    const [createModal, setCreateModal]   = useState(false);
    const [editModal, setEditModal]       = useState(false);
    const [viewDetailModal, setViewDetailModal] = useState(false);
    const [viewDetailLoading, setViewDetailLoading] = useState(false);
    const [selectedMapping, setSelectedMapping] = useState(null);
    const { roles, loading: rolesLoading }      = useUserRoles();
    const [domains, setDomains]           = useState([]);
    const [domainsLoading, setDomainsLoading]   = useState(false);
    const [createForm, setCreateForm]     = useState({ roleId: '', domainId: '', createdBy: '', isReadOnly: false, isReadWrite: true, isEnabled: true });
    const [editForm, setEditForm]         = useState({ id: null, updatedBy: '', isReadOnly: false, isReadWrite: false, isEnabled: true });
    const [createErr, setCreateErr]       = useState('');
    const [editErr, setEditErr]           = useState('');
    const [createSaving, setCreateSaving] = useState(false);
    const [editSaving, setEditSaving]     = useState(false);
    const [CurrentDomain,setCurrentDomain] = useState(sessionStorage.getItem("selectedDomain")|| "");

    useEffect(() => { fetchDataGrid(); fetchDomains(); }, []);

    const fetchDataGrid = async () => {
        setLoading(true);
        try { const data = await getAllRoleDomainMappings(CurrentDomain); setMappings(data || []); }
        catch (err) { showToast(err.response?.data?.message || err.message || 'Failed to load records', 'error'); }
        finally { setLoading(false); }
    };

    const fetchDomains = async () => {
        setDomainsLoading(true);
        try { const data = await getAllDomainsLookup(CurrentDomain); setDomains(data || []); }
        catch (err) { showToast(err.response?.data?.message || err.message || 'Failed to load domains', 'error'); }
        finally { setDomainsLoading(false); }
    };

    const showToast = (message, type = 'success') => {
        setToast({ visible: true, message, type });
        setTimeout(() => setToast(t => ({ ...t, visible: false })), 3000);
    };

    const stats = {
        total:   mappings.length,
        enabled: mappings.filter(m => m.isEnabled).length,
        rw:      mappings.filter(m => m.isReadWrite).length,
        domains: new Set(mappings.map(m => m.domainId)).size,
    };

    const filtered = mappings.filter(m => {
        const q = search.toLowerCase();
        const matchQ = !q || (m.roleName || '').toLowerCase().includes(q) || (m.domainName || '').toLowerCase().includes(q);
        const matchS = !filterStatus || (filterStatus === 'enabled' ? m.isEnabled : !m.isEnabled);
        return matchQ && matchS;
    });

    const handleInspectId = async (id) => {
        setViewDetailModal(true);
        setViewDetailLoading(true);
        setSelectedMapping(null);
        try { const data = await getRoleDomainMappingById(id, CurrentDomain); setSelectedMapping(data); }
        catch { showToast('Could not retrieve record.', 'error'); setViewDetailModal(false); }
        finally { setViewDetailLoading(false); }
    };

    const openCreate = () => {
        setCreateForm({ roleId: '', domainId: '', createdBy: getSessionUsername(), isReadOnly: false, isReadWrite: true, isEnabled: true });
        setCreateErr('');
        setCreateModal(true);
    };

    const handleCreate = async () => {
        const roleId   = parseInt(createForm.roleId, 10);
        const domainId = parseInt(createForm.domainId, 10);
        if (!roleId || !domainId) { setCreateErr('Please select both a Role and a Domain.'); return; }
        setCreateSaving(true);
        try {
            await createRoleDomainMapping({ roleId, domainId, isReadOnly: createForm.isReadOnly, isReadWrite: createForm.isReadWrite, isEnabled: createForm.isEnabled, createdBy: createForm.createdBy });
            setCreateModal(false);
            showToast('Mapping created successfully');
            fetchDataGrid();
            window.dispatchEvent(new Event('domains-updated'));
        } catch (err) { setCreateErr(err.response?.data?.message || err.message || 'Submission failed.'); }
        finally { setCreateSaving(false); }
    };

    const openEdit = (item) => {
        setEditForm({ id: item.id, updatedBy: getSessionUsername(), isReadOnly: item.isReadOnly, isReadWrite: item.isReadWrite, isEnabled: item.isEnabled });
        setEditErr('');
        setEditModal(true);
    };

    const handleUpdate = async () => {
        setEditSaving(true);
        try {
            await updateRoleDomainMapping(editForm.id,CurrentDomain ,{ isReadOnly: editForm.isReadOnly, isReadWrite: editForm.isReadWrite, isEnabled: editForm.isEnabled, updatedBy: editForm.updatedBy });
            setEditModal(false);
            showToast('Mapping updated successfully');
            fetchDataGrid();
            window.dispatchEvent(new Event('domains-updated'));
        } catch (err) { setEditErr(err.response?.data?.message || err.message || 'Update failed.'); }
        finally { setEditSaving(false); }
    };

    const thStyle = {
        padding: '10px 14px', textAlign: 'left', fontWeight: 600,
        color: c.muted, fontSize: '11px', textTransform: 'uppercase',
        letterSpacing: '0.6px', background: '#F8FAFC',
        borderBottom: `1px solid ${c.border}`, whiteSpace: 'nowrap',
        position: 'sticky', top: 0, zIndex: 1,
    };

    return (
        <div style={{ maxWidth: '100%', margin: '0 auto', padding: '0.75rem 1rem', fontFamily: sans, fontSize: '14px', color: c.ink, background: c.pageBg, minHeight: '100vh' }}>
            <GlobalKeyframes />

            {/* ── Page header ── */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.9rem', flexWrap: 'wrap', gap: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{
                        width: '42px', height: '42px', borderRadius: '12px',
                        background: `linear-gradient(135deg, ${c.accent} 0%, #059669 100%)`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '20px', boxShadow: '0 3px 10px rgba(10,163,117,0.3)',
                    }}>🔐</div>
                    <div>
                        <div style={{ fontSize: '18px', fontWeight: 700, color: c.ink, letterSpacing: '-0.3px' }}>Role Domain Mappings</div>
                        <div style={{ fontSize: '12px', color: c.muted, marginTop: '1px' }}>Manage access control across roles and domains</div>
                    </div>
                </div>
                <button style={btnPrimary} onClick={openCreate}>
                    <span style={{ fontSize: '17px', lineHeight: 1 }}>+</span>
                </button>
            </div>

            <div style={{
                background: c.surface, border: `1px solid ${c.border}`, borderRadius: '14px',
                padding: '10px 14px', display: 'flex', alignItems: 'center', gap: '10px',
                flexWrap: 'wrap', marginBottom: '12px',
                boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
            }}>
                {/* Stat cards */}
                <StatCard label="Total"          value={stats.total}   icon="📋" bg="#F8FAFC"       iconBg="#E2E8F0" iconColor={c.muted}   borderColor={c.border}        loading={loading} />
                <StatCard label="Enabled"        value={stats.enabled} icon="✅" bg={c.successLight} iconBg="#D1FAE5" iconColor={c.success} borderColor={c.successBorder} loading={loading} />
                <StatCard label="Read / Write"   value={stats.rw}      icon="✏️" bg="#EEF2FF"        iconBg="#C7D2FE" iconColor="#4F46E5"   borderColor="#A5B4FC"         loading={loading} />
                <StatCard label="Unique Domains" value={stats.domains} icon="🌐" bg={c.infoLight}    iconBg="#BAE6FD" iconColor={c.info}    borderColor="#7DD3FC"         loading={loading} />

                <div style={{ width: '1px', height: '44px', background: c.border, flexShrink: 0, marginLeft: '2px', marginRight: '2px' }} />

                <div style={{ position: 'relative', flex: 1, minWidth: '160px' }}>
                    <span style={{ position: 'absolute', left: '11px', top: '50%', transform: 'translateY(-50%)', color: c.faint, fontSize: '13px', pointerEvents: 'none' }}>⌕</span>
                    <input
                        type="text"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder="Search role or domain…"
                        style={{ ...inputStyle, background: c.pageBg, padding: '8px 12px 8px 30px' }}
                    />
                </div>

                <select
                    value={filterStatus}
                    onChange={e => setFilterStatus(e.target.value)}
                    style={{ ...inputStyle, width: 'auto', minWidth: '140px', cursor: 'pointer', background: c.pageBg, padding: '8px 12px' }}
                >
                    <option value="">All statuses</option>
                    <option value="enabled">Enabled only</option>
                    <option value="disabled">Disabled only</option>
                </select>

                {(search || filterStatus) && (
                    <button
                        style={{ ...btnBase, padding: '7px 12px', fontSize: '12px', flexShrink: 0 }}
                        onClick={() => { setSearch(''); setFilterStatus(''); }}
                    >
                        ✕ Clear
                    </button>
                )}

                <div style={{ fontSize: '12px', color: c.faint, fontFamily: mono, whiteSpace: 'nowrap', marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {loading ? <InlineSpinnerText label="Loading…" size={12} /> : `${filtered.length} / ${mappings.length} records`}
                </div>
            </div>

            <div style={{ background: c.surface, border: `1px solid ${c.border}`, borderRadius: '14px', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.05)', position: 'relative' }}>
                <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '520px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', minWidth: '760px' }}>
                        <thead>
                            <tr>
                                {/* <th style={{ ...thStyle, width: '60px' }}>ID</th> */}
                                <th style={thStyle}>Role</th>
                                <th style={thStyle}>Domain</th>
                                <th style={{ ...thStyle, textAlign: 'center' }}>Read only</th>
                                <th style={{ ...thStyle, textAlign: 'center' }}>Read / Write</th>
                                <th style={thStyle}>Status</th>
                                <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={7} style={{ padding: '4rem 1rem' }}>
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '14px' }}>
                                            <Spinner size={30} thickness={3} />
                                            <div style={{ color: c.muted, fontSize: '13px', fontWeight: 500 }}>Loading mapping records…</div>
                                        </div>
                                    </td>
                                </tr>
                            ) : filtered.length === 0 ? (
                                <tr>
                                    <td colSpan={7} style={{ textAlign: 'center', padding: '4rem 1rem', color: c.muted }}>
                                        <div style={{ fontSize: '30px', marginBottom: '8px', opacity: 0.4 }}>🔍</div>
                                        No mappings match your filters.
                                    </td>
                                </tr>
                            ) : filtered.map((m, i) => (
                                <tr
                                    key={m.id}
                                    style={{
                                        borderBottom: `1px solid ${c.borderSoft}`, background: c.surface, transition: 'background 0.1s',
                                        animation: `rdm-fade-in 0.25s ease ${Math.min(i, 12) * 0.02}s both`,
                                    }}
                                    onMouseEnter={e => e.currentTarget.style.background = '#F8FAFC'}
                                    onMouseLeave={e => e.currentTarget.style.background = c.surface}
                                >
                                    {/* <td style={{ padding: '10px 14px', color: c.faint, fontSize: '12px', fontFamily: mono }}>
                                        <span style={{ background: c.pageBg, border: `1px solid ${c.border}`, borderRadius: '6px', padding: '2px 7px' }}>#{m.id}</span>
                                    </td> */}
                                    <td style={{ padding: '10px 14px' }}>
                                        <div style={{ fontWeight: 600, color: c.ink }}>{m.roleName || `Role #${m.roleId}`}</div>
                                        {/* <div style={{ fontSize: '11px', color: c.faint, fontFamily: mono, marginTop: '1px' }}>id:{m.roleId}</div> */}
                                    </td>
                                    <td style={{ padding: '10px 14px' }}>
                                        <div style={{ color: c.inkSoft, fontWeight: 500 }}>{m.domainName || `Domain #${m.domainId}`}</div>
                                        {/* <div style={{ fontSize: '11px', color: c.faint, fontFamily: mono, marginTop: '1px' }}>id:{m.domainId}</div> */}
                                    </td>
                                    <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                                        <Badge type={m.isReadOnly ? 'accent' : 'neutral'} label={m.isReadOnly ? 'Yes' : 'No'} />
                                    </td>
                                    <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                                        <Badge type={m.isReadWrite ? 'accent' : 'neutral'} label={m.isReadWrite ? 'Yes' : 'No'} />
                                    </td>
                                    <td style={{ padding: '10px 14px' }}>
                                        <Badge type={m.isEnabled ? 'success' : 'danger'} label={m.isEnabled ? 'Active' : 'Suspended'} />
                                    </td>
                                    <td style={{ padding: '10px 14px', textAlign: 'right' }}>
                                        <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                                            <button
                                                style={iconBtn}
                                                onClick={() => handleInspectId(m.id)}
                                                title="View details"
                                                onMouseEnter={e => { e.currentTarget.style.background = c.accentLight; e.currentTarget.style.borderColor = c.accentMid; e.currentTarget.style.color = c.accent; }}
                                                onMouseLeave={e => { e.currentTarget.style.background = c.surface; e.currentTarget.style.borderColor = c.border; e.currentTarget.style.color = c.muted; }}
                                            >👁</button>
                                            <button
                                                style={iconBtn}
                                                onClick={() => openEdit(m)}
                                                title="Edit mapping"
                                                onMouseEnter={e => { e.currentTarget.style.background = c.accentLight; e.currentTarget.style.borderColor = c.accentMid; e.currentTarget.style.color = c.accent; }}
                                                onMouseLeave={e => { e.currentTarget.style.background = c.surface; e.currentTarget.style.borderColor = c.border; e.currentTarget.style.color = c.muted; }}
                                            >✏️</button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <Modal
                visible={createModal}
                onClose={() => !createSaving && setCreateModal(false)}
                title="New Role–Domain Mapping"
                footer={
                    <>
                        <button style={btnBase} onClick={() => setCreateModal(false)} disabled={createSaving}>Cancel</button>
                        <button style={{ ...btnPrimary, opacity: createSaving ? 0.75 : 1, cursor: createSaving ? 'default' : 'pointer' }} onClick={handleCreate} disabled={createSaving}>
                            {createSaving && <Spinner size={14} color="#fff" trackColor="rgba(255,255,255,0.35)" thickness={2} />}
                            {createSaving ? 'Creating…' : 'Create mapping'}
                        </button>
                    </>
                }
            >
                <FormField label="Role">
                    <select
                        style={{ ...inputStyle, cursor: 'pointer' }}
                        value={createForm.roleId || ''}
                        onChange={e => setCreateForm(f => ({ ...f, roleId: e.target.value }))}
                        disabled={rolesLoading}
                    >
                        <option value="">{rolesLoading ? 'Loading roles…' : 'Select a role'}</option>
                        {!rolesLoading && roles.map(r => <option key={r.id} value={r.id}>{r.text} (ID: {r.id})</option>)}
                    </select>
                    {rolesLoading && <div style={{ marginTop: '6px' }}><InlineSpinnerText label="Fetching roles…" /></div>}
                </FormField>
                <FormField label="Domain">
                    <select
                        style={{ ...inputStyle, cursor: 'pointer' }}
                        value={createForm.domainId || ''}
                        onChange={e => setCreateForm(f => ({ ...f, domainId: e.target.value }))}
                        disabled={domainsLoading}
                    >
                        <option value="">{domainsLoading ? 'Loading domains…' : 'Select a domain'}</option>
                        {!domainsLoading && domains.map(d => <option key={d.id} value={d.id}>{d.text} (ID: {d.id})</option>)}
                    </select>
                    {domainsLoading && <div style={{ marginTop: '6px' }}><InlineSpinnerText label="Fetching domains…" /></div>}
                </FormField>
                <FormField label="Permissions">
                    <div style={{ border: `1.5px solid ${c.border}`, borderRadius: '12px', padding: '0 14px', background: c.pageBg }}>
                        <ToggleRow label="Read-only"  desc="Can view but not modify" checked={createForm.isReadOnly}  onChange={e => setCreateForm(f => ({ ...f, isReadOnly: e.target.checked }))} />
                        <ToggleRow label="Read/write" desc="Can view and modify"     checked={createForm.isReadWrite} onChange={e => setCreateForm(f => ({ ...f, isReadWrite: e.target.checked }))} />
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 0' }}>
                            <div>
                                <div style={{ fontSize: '13px', fontWeight: 500, color: c.inkSoft }}>Active on creation</div>
                                <div style={{ fontSize: '11px', color: c.faint, marginTop: '1px' }}>Mapping will be enabled immediately</div>
                            </div>
                            <Toggle checked={createForm.isEnabled} onChange={e => setCreateForm(f => ({ ...f, isEnabled: e.target.checked }))} />
                        </div>
                    </div>
                </FormField>
                <div style={{ fontSize: '11.5px', color: c.faint, marginTop: '-4px', marginBottom: '4px' }}>
                    Creating as <b style={{ color: c.muted }}>{createForm.createdBy}</b>
                </div>
                {createErr && (
                    <div style={{ color: c.danger, background: c.dangerLight, border: `1px solid ${c.dangerBorder}`, borderRadius: '8px', padding: '9px 12px', fontSize: '12.5px', marginTop: '8px' }}>
                        {createErr}
                    </div>
                )}
            </Modal>

            <Modal
                visible={editModal}
                onClose={() => !editSaving && setEditModal(false)}
                title={<>Edit mapping <span style={{ color: c.faint, fontSize: '14px', fontWeight: 400, fontFamily: mono }}>#{editForm.id}</span></>}
                footer={
                    <>
                        <button style={btnBase} onClick={() => setEditModal(false)} disabled={editSaving}>Cancel</button>
                        <button style={{ ...btnPrimary, opacity: editSaving ? 0.75 : 1, cursor: editSaving ? 'default' : 'pointer' }} onClick={handleUpdate} disabled={editSaving}>
                            {editSaving && <Spinner size={14} color="#fff" trackColor="rgba(255,255,255,0.35)" thickness={2} />}
                            {editSaving ? 'Saving…' : 'Save changes'}
                        </button>
                    </>
                }
            >
                <FormField label="Permissions">
                    <div style={{ border: `1.5px solid ${c.border}`, borderRadius: '12px', padding: '0 14px', background: c.pageBg }}>
                        <ToggleRow label="Read-only"  desc="Can view but not modify" checked={editForm.isReadOnly}  onChange={e => setEditForm(f => ({ ...f, isReadOnly: e.target.checked }))} />
                        <ToggleRow label="Read/write" desc="Can view and modify"     checked={editForm.isReadWrite} onChange={e => setEditForm(f => ({ ...f, isReadWrite: e.target.checked }))} />
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 0' }}>
                            <div>
                                <div style={{ fontSize: '13px', fontWeight: 500, color: c.inkSoft }}>Active</div>
                                <div style={{ fontSize: '11px', color: c.faint, marginTop: '1px' }}>Enable or suspend this mapping</div>
                            </div>
                            <Toggle checked={editForm.isEnabled} onChange={e => setEditForm(f => ({ ...f, isEnabled: e.target.checked }))} />
                        </div>
                    </div>
                </FormField>
                <div style={{ fontSize: '11.5px', color: c.faint, marginTop: '-4px', marginBottom: '4px' }}>
                    Updating as <b style={{ color: c.muted }}>{editForm.updatedBy}</b>
                </div>
                {editErr && (
                    <div style={{ color: c.danger, background: c.dangerLight, border: `1px solid ${c.dangerBorder}`, borderRadius: '8px', padding: '9px 12px', fontSize: '12.5px', marginTop: '8px' }}>
                        {editErr}
                    </div>
                )}
            </Modal>

            <Modal
                visible={viewDetailModal}
                onClose={() => setViewDetailModal(false)}
                title={<>Mapping detail {selectedMapping && <span style={{ color: c.faint, fontSize: '14px', fontWeight: 400, fontFamily: mono }}>#{selectedMapping?.id}</span>}</>}
                footer={<button style={btnBase} onClick={() => setViewDetailModal(false)}>Close</button>}
            >
                {viewDetailLoading ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', padding: '2.5rem 0' }}>
                        <Spinner size={26} thickness={3} />
                        <div style={{ color: c.muted, fontSize: '13px' }}>Fetching record…</div>
                    </div>
                ) : selectedMapping && (
                    <div style={{ fontSize: '13px' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '14px' }}>
                            <div style={{ background: c.accentLight, border: `1px solid ${c.accentMid}`, borderRadius: '10px', padding: '12px' }}>
                                <div style={{ fontSize: '10px', fontWeight: 600, color: c.accent, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Role</div>
                                <div style={{ fontWeight: 700, color: c.ink }}>{selectedMapping.roleName || '—'}</div>
                                <div style={{ fontSize: '11px', color: c.faint, fontFamily: mono }}>ID: {selectedMapping.roleId}</div>
                            </div>
                            <div style={{ background: c.infoLight, border: `1px solid #BAE6FD`, borderRadius: '10px', padding: '12px' }}>
                                <div style={{ fontSize: '10px', fontWeight: 600, color: c.info, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Domain</div>
                                <div style={{ fontWeight: 700, color: c.ink }}>{selectedMapping.domainName || '—'}</div>
                                <div style={{ fontSize: '11px', color: c.faint, fontFamily: mono }}>ID: {selectedMapping.domainId}</div>
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: '8px', marginBottom: '14px', flexWrap: 'wrap' }}>
                            <Badge type={selectedMapping.isReadOnly  ? 'accent'  : 'neutral'} label={`Read-only: ${selectedMapping.isReadOnly  ? 'Yes' : 'No'}`} />
                            <Badge type={selectedMapping.isReadWrite ? 'accent'  : 'neutral'} label={`Read/write: ${selectedMapping.isReadWrite ? 'Yes' : 'No'}`} />
                            <Badge type={selectedMapping.isEnabled   ? 'success' : 'danger'}  label={selectedMapping.isEnabled ? 'Active' : 'Suspended'} />
                        </div>
                        <div style={{ background: c.pageBg, border: `1px solid ${c.border}`, borderRadius: '10px', padding: '12px', fontSize: '12px', color: c.muted, lineHeight: 1.7 }}>
                            <div>Created by <b style={{ color: c.inkSoft }}>{selectedMapping.createdBy || 'System'}</b> · {selectedMapping.createdDate}</div>
                            {selectedMapping.updatedBy && (
                                <div>Updated by <b style={{ color: c.inkSoft }}>{selectedMapping.updatedBy}</b> · {selectedMapping.updatedDate}</div>
                            )}
                        </div>
                    </div>
                )}
            </Modal>

            <Toast message={toast.message} type={toast.type} visible={toast.visible} />
        </div>
    );
};

export default RoleDomainMappings;